//@ts-nocheck
import formElementMixin from "@banno/jha-wc/src/js/mixins/form-element-mixin";
import Router from "@jack-henry/web-component-router";
import { LitElement, css, html } from "lit";
import { customElement, property } from "lit/decorators.js";
import { routerConfig } from "../../routers";
import { getRequest, postRequest } from "../../utils/jha-fetch";
import { Apis } from "../../utils/api-constants";
import { GlobalVariables } from "../../utils/constants";
import "@banno/jha-wc/src/forms/jha-form-select/jha-form-select";
import "@banno/jha-wc/src/forms/jha-form-input-base/jha-form-input";
import "@banno/jha-wc/src/forms/jha-form-radio-button/jha-form-radio-button";
import "@banno/jha-wc/src/forms/jha-form-checkbox/jha-form-checkbox";
import "@banno/jha-wc/src/forms/jha-form-textarea-input/jha-form-textarea-input";
import "@banno/jha-wc/src/forms/jha-form-email-input/jha-form-email-input";
import "@banno/jha-wc/src/forms/jha-form-text-input/jha-form-text-input";
import { AddUserType } from "../../types/Users/AddUserType";
import { showToast } from "@banno/jha-wc/src/js/utils/toast-util.js";
import { getErrorMessage } from "../../utils/getErrorMessage";
import { filterByUserType } from "../../utils/filterByUserType";
import "../../Components/Loader";
import { getRoleData } from "../../utils/getRoleData";
import { decryptValue, stringComparison } from "../../utils/app-utils";
import Cookies from "js-cookie";
import { AppRouters } from "../../utils/app-router";
import { openJhftDialog } from "../../Components/JhftJhaDialog";
import {
  applyUserFormBaselineDefaults,
  captureUserFormBaseline,
  isUserFormDirty,
} from "../../utils/user-form-dirty";
import { getBusinessUnitName } from "../../utils/getBusinessUnitName";

/** Cloud FTP CreateUser: start with a-z, then a-z / 0-9 / hyphen, max 32. ACME-1941. */
export const CLOUD_FTP_USER_ID_RE = /^[a-z][a-z0-9-]{0,31}$/;
export const CLOUD_FTP_USER_ID_MAX_LEN = 32;

export function normalizeCloudFtpUserId(value: unknown): string {
  return String(value ?? "").toLowerCase();
}

export function isCloudFtpUserId(value: unknown): boolean {
  return CLOUD_FTP_USER_ID_RE.test(normalizeCloudFtpUserId(value));
}

@customElement("add-new-user-form")
export class AddNewUserForm extends formElementMixin(LitElement) {
  constructor() {
    super();
    this.cacheId = "add-new-user-form";
    this.formData = {
      fullName: "",
      userId: "",
      email: "",
      businessUnit: "",
      homeDirectory: "",
      note: "",
      role: "",
      userType: "",
      authType: "",
      userProfileType: "",
    };
  }
  @property()
  addProfType = "";

  @property({ type: Array })
  userTypes = [];
  @property({ type: Array })
  userRoles = [];
  @property({ type: Array })
  businessUnits = [];

  @property({ type: Boolean })
  apiLoading = false;
  @property({ type: Boolean })
  isFolderLocationValid = false;
  @property({ type: Boolean })
  isUserIdValid = false;
  @property()
  loggedUserBu = Cookies.get("businessUnit");
  @property()
  loggedUserType = decryptValue(Cookies.get("userType"));
  @property({ type: Boolean })
  isEditable = /readonly/i.test(decryptValue(Cookies.get("userRole")));
  @property({ type: Boolean })
  isPasswordChecked = true;
  @property()
  selectedUserRole = "";

  /** ACME-1050 Cancel dirty-gate — see `src/utils/user-form-dirty.ts`. */
  _cancelBaseline: string | null = null;

  static get styles() {
    return [
      css`
        @media (prefers-color-scheme: dark) {
          h2 {
            color: #a3d4ff;
          }
        }
        @media (prefers-color-scheme: light) {
          h2 {
            color: #06185f;
          }
        }
        :host {
          overflow: visible;
        }
        h2 {
          margin-bottom: 10px;
          font-size: 24px;
          font-weight: 600;
        }
        #txtAreaDiv,
        #textAreaDiv {
          width: 500px;
        }
        /* jha-form-textarea-input has no :host display rule, so width is ignored
           until it is a block. */
        #txtAreaDiv jha-form-text-input,
        #textAreaDiv jha-form-textarea-input {
          display: block;
          width: 100%;
        }
        #textAreaDiv {
          min-height: 150px;
          resize: auto;
        }
        #textFieldDiv {
          max-width: 40%;
        }
        #rowRiv {
          overflow: visible;
        }
        #rowDiv {
          margin-bottom: 24px;
        }
        #formTable {
          display: grid;
          /* minmax(0, 1fr): fixed halves so a validation message can't widen its column. */
          grid-template-columns: repeat(2, minmax(0, 1fr));
          column-gap: 20px;
          row-gap: 20px;
          overflow: visible;
        }
        #radioSpan {
          display: flex;
          flex-direction: column;
        }
        jha-form-radio-button {
          width: 40%;
        }
        form {
          overflow: visible;
        }
      `,
    ];
  }
  @property()
  pageTitle = "Add User";

  async connectedCallback() {
    super.connectedCallback();

    // Clear the session storage cache for this form to start fresh
    sessionStorage.removeItem(this.cacheId);

    // Reset userType to ensure it starts empty
    this.formData.userType = "";
    this.formData.role = "";

    // Force update to re-render with empty userType
    this.requestUpdate();

    // Capture before the lookups below: the form is already typable while they run, and a
    // null baseline reads as clean, so Cancel would drop those edits with no prompt.
    this._cancelBaseline = captureUserFormBaseline(this.formData);

    document.title = document.title.concat(" >", this.pageTitle);

    const userRoleRes = await getRequest(Apis.USER_ROLE);
    enum UserRoles {
      readonly = "Read Only",
      readwrite = "Read/Write",
    }
    const userRoles = getRoleData(userRoleRes?.list ?? []);
    this.userRoles = userRoles.map((userRole) => {
      userRole.name = UserRoles[userRole.value];
      return userRole;
    });

    const userTypesRes = await getRequest(Apis.USER_TYPE);
    enum UserTypes {
      participant = "Participant",
      businessadmin = "Business Admin",
      enterpriseadmin = "Enterprise Admin",
    }
    this.userTypes = getRoleData(userTypesRes?.list ?? []).map((userType) => {
      userType.name = UserTypes[userType.value];
      return userType;
    });
    const businessUnitsAllRes = await getRequest(
      Apis.GET_ALL_BUSINESSUNITVALUES
    );
    const businessUnitsRes = businessUnitsAllRes?.businessUnitValues ?? [];
    this.businessUnits = businessUnitsRes
      .filter(
        (bu) =>
          bu.status === "active" ||
          bu.value.toLowerCase() === this.loggedUserBu?.toLowerCase()
      )
      .map((y) => {
        return { value: y.value, name: y.name, status: y.status };
      })
      .sort((a, b) => a.name.localeCompare(b.name));

    this.isFolderLocationValid = false;
    if (stringComparison(this.loggedUserType, "BusinessAdmin")) {
      const isActivebu = this.businessUnits.find(
        (r) =>
          r.status === "active" &&
          r.value.toLowerCase() === this.loggedUserBu?.toLowerCase()
      );
      if (isActivebu) {
        this.formData.businessUnit = this.loggedUserBu?.toLowerCase();
      } else {
        this.formData.businessUnit = "";
      }
      this.userTypes = filterByUserType(this.userTypes);
    } else {
      this.formData.businessUnit = "";
    }
    this.formData.authType = "password"; //initial authtype value

    // Fold in only what this method just assigned. Re-snapshotting formData here would also
    // absorb anything the user typed while the lookups ran, hiding it from the dirty check.
    this._cancelBaseline = applyUserFormBaselineDefaults(this._cancelBaseline, {
      businessUnit: this.formData.businessUnit,
      authType: this.formData.authType,
    });
  }

  async onCancelClick() {
    const leave = () => {
      const router = new Router(routerConfig);
      router.go("/users");
    };

    // isEditable is really "is read-only" here (see its initializer) — those users cannot
    // have changed anything. Otherwise: no change, no prompt.
    if (this.isEditable || !isUserFormDirty(this._cancelBaseline, this.formData)) {
      leave();
      return;
    }

    const result = await openJhftDialog({
      variant: "warning",
      title: "Unsaved Changes",
      message: "You have unsaved changes. Are you sure you want to leave without saving?",
      cancelLabel: "Stay",
      confirmLabel: "Leave Without Saving",
    });

    if (result === "confirm") {
      leave();
    }
  }

  async dispatchAndGo() {
    this.dispatchEvent(
      new CustomEvent("disable-unload-handler", {
        bubbles: true,
        composed: true,
      })
    );
    AppRouters.go("/users");
  }

  async onAddUserClick() {
    this.dispatchEvent(
      new CustomEvent("disable-unload-handler", {
        bubbles: true,
        composed: true,
      })
    );
    this.apiLoading = true;
    this.formData.userProfileType = this.addProfType;
    this.formData.userId = normalizeCloudFtpUserId(this.formData.userId);
    if (!isCloudFtpUserId(this.formData.userId)) {
      this.apiLoading = false;
      return;
    }
    const usesFolderLocation =
      stringComparison(this.addProfType, "regular") ||
      stringComparison(this.addProfType, "destination");
    if (usesFolderLocation && this.formData.homeDirectory) {
      let homeDirectory = this.formData.homeDirectory;
      homeDirectory = homeDirectory.startsWith("/")
        ? homeDirectory.slice(1)
        : homeDirectory;
      homeDirectory = homeDirectory.endsWith("/")
        ? homeDirectory.slice(0, -1)
        : homeDirectory;
      this.formData.homeDirectory = homeDirectory;
    } else {
      this.formData.homeDirectory = "";
    }
    this.formData.userType = stringComparison(this.addProfType, "regular")
      ? this.formData.userType
      : "participant";
    const reqParams = stringComparison(this.addProfType, "regular")
      ? {
        businessUnit: this.formData.businessUnit,
        role: this.formData.role,
        fullName: this.formData.fullName,
        userId: this.formData.userId,
        email: this.formData.email,
        homeDirectory: this.formData.homeDirectory,
        note: this.formData.note,
        userProfileType: this.addProfType,
        userType: this.formData.userType,
        authType: this.formData.authType,
      }
      : {
        businessUnit: this.formData.businessUnit,
        role: this.formData.role,
        fullName: this.formData.fullName,
        userId: this.formData.userId,
        email: this.formData.email,
        homeDirectory: this.formData.homeDirectory,
        userProfileType: "destination",
        userType: "participant",
        authType: this.formData.authType,
      };

    const formDataSubmit = await postRequest<AddUserType>(
      Apis.ADD_USER,
      reqParams
    );
    if (formDataSubmit?.messageStatus === null) {
      this.apiLoading = false;

      showToast({
        message: "A new user has been created successfully.",
        role: "success",
        duration: GlobalVariables.TOAST_TIMEOUT,
      });

      const router = new Router(routerConfig);
      router.go("/users");
    } else if (formDataSubmit.details.length > 0) {
      this.apiLoading = false;

      const errMsg = getErrorMessage(formDataSubmit.details);
      showToast({
        message: errMsg,
        role: "danger",
        duration: GlobalVariables.TOAST_TIMEOUT,
      });
    } else {
      this.apiLoading = false;

      showToast({
        message: "Something went wrong, please try again.",
        role: "danger",
        duration: GlobalVariables.TOAST_TIMEOUT,
      });
    }
  }

  onFolderLocationChange(e) {
    const regex = /^\/([a-zA-Z0-9#_\-]+\/)*[a-zA-Z0-9#_\-]+\/$/;
    let strRegex = /^[a-zA-Z0-9]+$/;
    let inValid: boolean = false;
    if (!regex.test(e.target.value)) {
      inValid = true;
    } else {
      var innerValues = e.target.value?.split("/");
      innerValues.forEach((element) => {
        if (
          element !== "" &&
          (!strRegex.test(element[0]) || !strRegex.test(element.slice(-1)))
        ) {
          inValid = true;
        }
      });
    }

    const invalidField = this.shadowRoot.querySelector(
      "jha-form-text-input[data-auto-id='homeDirectoryInput']"
    );
    if (invalidField) {
      if (
        !stringComparison(this.formData.userType, "enterpriseadmin") &&
        inValid
      ) {
        invalidField.warning = "Please enter a valid input.";
        invalidField.showWarning = true;
        this.isFolderLocationValid = false;
        this.updateProperty("homeDirectory", e.target.value);
      } else {
        invalidField.showWarning = false;
        this.isFolderLocationValid = true;
        this.updateProperty("homeDirectory", e.target.value);
      }
    }
  }

  onUserTypeChange(e) {
    if (stringComparison(e.target.value, "enterpriseadmin")) {
      if (this.businessUnits.find((r) => r.value === "all")) {
      } else {
        const rec = { name: "All", value: "all" };
        this.businessUnits.push(rec);
      }
      this.updateProperty("businessUnit", "all");
      this.updateProperty("homeDirectory", "");
      this.isFolderLocationValid = true;
    } else if (stringComparison(e.target.value, "businessadmin")) {
      this.businessUnits = this.businessUnits.filter(
        (r) => r.value !== "all" && r.status === "active"
      );
      if (stringComparison(this.loggedUserType, "BusinessAdmin")) {
        const isActivebu = this.businessUnits.find(
          (r) =>
            r.status === "active" &&
            r.value.toLowerCase() === this.loggedUserBu?.toLowerCase()
        );
        if (isActivebu) {
          this.formData.businessUnit = this.loggedUserBu?.toLowerCase();
          this.updateProperty("businessUnit", this.loggedUserBu.toLowerCase());
        } else {
          this.formData.businessUnit = "";
          this.updateProperty("businessUnit", "");
        }
      } else {
        this.formData.businessUnit = "";
        this.updateProperty("businessUnit", "");
      }
      this.updateProperty("homeDirectory", "");
      this.isFolderLocationValid = true;
    } else {
      this.businessUnits = this.businessUnits.filter(
        (r) => r.value !== "all" && r.status === "active"
      );
      if (stringComparison(this.loggedUserType, "BusinessAdmin")) {
        const isActivebu = this.businessUnits.find(
          (r) =>
            r.status === "active" &&
            r.value.toLowerCase() === this.loggedUserBu?.toLowerCase()
        );
        if (isActivebu) {
          this.formData.businessUnit = this.loggedUserBu?.toLowerCase();
          this.updateProperty("businessUnit", this.loggedUserBu.toLowerCase());
        } else {
          this.formData.businessUnit = "";
          this.updateProperty("businessUnit", "");
        }
      } else {
        this.formData.businessUnit = "";
        this.updateProperty("businessUnit", "");
      }
      this.isFolderLocationValid = false;
    }
    this.updateProperty("userType", e.target.value);
    const userTypeToCheck = stringComparison(this.addProfType, "regular")
      ? e.target.value
      : "participant";

    if (!stringComparison(userTypeToCheck, "participant")) {
      this.formData.authType = "password";
    }
    this.requestUpdate();
    this.updateComplete.then(() => {
      // After re-render, clear the home directory input if it exists
      const homeDirectoryInput = this.shadowRoot.querySelector(
        "jha-form-text-input[data-auto-id='homeDirectoryInput']"
      );
      if (homeDirectoryInput) {
        homeDirectoryInput.value = "";
      }
      this.validateForm();
    });
  }

  onAuthModeChange(e: { target: { value: string } }) {
    this.formData.authType = e.target.value;
    this.requestUpdate();
  }

  onUserIdChange(e) {
    const field = e.target;
    const lowered = normalizeCloudFtpUserId(e.detail ?? field?.value);
    // Write the lowered value onto the inner input; skip the public setter so we
    // don't dispatch a second change (JhaFormInput.updated fires one when _modified).
    if (field && String(field.value) !== lowered) {
      field.value_ = lowered;
      if (field.input_) {
        field.input_.value = lowered;
      }
    }
    const valid = isCloudFtpUserId(lowered);
    this.isUserIdValid = valid;
    // jha-form-text-input never puts `pattern` on its inner <input> (only `required`
    // is in its render). Native pattern checks never run — same showWarning path as Folder Location.
    if (field) {
      field.showWarning = !valid;
    }
    this.updateProperty("userId", lowered);
  }

  onUserRoleChange(e) {
    this.updateProperty("role", e.target.value);
    this.requestUpdate();
    this.updateComplete.then(() => this.validateForm());
  }

  /** Destination has no User Type field; userType stays empty until submit forces participant. */
  get shouldShowCredentials() {
    return (
      stringComparison(this.addProfType, "destination") ||
      stringComparison(this.formData.userType, "participant")
    );
  }

  get shouldShowFolderLocation() {
    if (stringComparison(this.addProfType, "destination")) {
      return true;
    }
    const isBusinessAdmin = stringComparison(
      this.formData.userType,
      "businessadmin"
    );
    const isEnterpriseAdmin = stringComparison(
      this.formData.userType,
      "enterpriseadmin"
    );
    // Show folder location for regular profiles unless Business Admin or Enterprise Admin is selected
    return !isBusinessAdmin && !isEnterpriseAdmin;
  }

  get isUserIdWarningVisible() {
    return Boolean(this.formData.userId) && !this.isUserIdValid;
  }

  get folderLocationTooltip() {
    return stringComparison(this.addProfType, "destination")
      ? "Folder path within the destination bucket. The folder is created under {BusinessUnitId}/FolderName if it does not already exist. Format: /FolderName/. Periods ('.') are not allowed. Max length of 256 characters."
      : "/Folder/path/ where the user will be able to access files. Folder will be created if it does not already exist. Format: FolderName/Path. Periods ('.') are not allowed. Max length of 256 characters.";
  }

  render() {
    return html`
      <div>
        ${this.apiLoading ? html`<circle-loader></circle-loader>` : undefined}
        <form>
          <div id="rowRiv">
            <jha-form-row>
              <h2>User Information</h2>
            </jha-form-row>
            <div id="formTable">
              ${stringComparison(this.addProfType, "regular")
        ? html`
                    <jha-tooltip
                      bottom
                      text="User Type determines the access levels for the user."
                    >
                      <jha-form-select
                        outline
                        label="User Type"
                        .options=${this.userTypes}
                        lableKey="name"
                        valueKey="value"
                        data-id="userType"
                        data-auto-id="userTypeSelect"
                        @change=${this.onUserTypeChange}
                        required
                        warning="User Type is required"
                        emptyOptionLabel="Select User Type"
                      >
                      </jha-form-select>
                    </jha-tooltip>
                  `
        : undefined}
              <jha-tooltip
                bottom
                text="User Role determines the permissions for the user."
              >
                <jha-form-select
                  outline
                  label="User Role"
                  .options=${this.userRoles}
                  lableKey="name"
                  valueKey="value"
                  data-id="role"
                  data-auto-id="roleSelect"
                  @change=${this.onUserRoleChange}
                  required
                  warning="User Role is required"
                  emptyOptionLabel="Select User Role"
                >
                </jha-form-select>
              </jha-tooltip>
              <jha-tooltip
                bottom
                text="Full Name of the user. Max length of 60 characters."
              >
                <jha-form-text-input
                  label="Full Name"
                  required
                  outline
                  maxlength="60"
                  charLimit="60"
                  placeholder="e.g., John Smith"
                  data-id="fullName"
                  data-auto-id="fullNameInput"
                  @change=${this.inputValueChange}
                  warning="Full Name is required"
                >
                </jha-form-text-input>
              </jha-tooltip>
              <jha-tooltip
                bottom
                text="User ID is the unique identifier for the user. Must start with a lowercase letter, then lowercase letters, digits, or hyphens only. Max 32 characters. Uppercase is converted to lowercase."
              >
                <jha-form-text-input
                  label="User ID"
                  required
                  outline
                  placeholder="e.g., abc-user"
                  data-id="userId"
                  data-auto-id="userIdInput"
                  @change=${this.onUserIdChange}
                  warning="User ID must start with a letter and use only lowercase letters, digits, or hyphens (max 32)."
                  .showWarning=${this.isUserIdWarningVisible}
                  maxlength=${CLOUD_FTP_USER_ID_MAX_LEN}
                  .characterLimit=${this.isUserIdWarningVisible
        ? null
        : CLOUD_FTP_USER_ID_MAX_LEN}
                >
                </jha-form-text-input>
              </jha-tooltip>
              <jha-tooltip
                bottom
                text="Email address of the user. Max length of 256 characters."
              >
                <jha-form-email-input
                  warning="Email Address is required"
                  placeholder="Eg: jsmith@domain.com"
                  type="email"
                  minlength="0"
                  maxlength="256"
                  role="textbox"
                  data-id="email"
                  data-auto-id="emailInput"
                  label="Email Address"
                  outline
                  required
                  @change=${this.inputValueChange}
                >
                </jha-form-email-input>
              </jha-tooltip>
              <jha-tooltip
                bottom
                text="Business Unit determines the access levels for the user."
              >
                <jha-form-select
                  outline
                  label="Business Unit"
                  .options=${this.businessUnits}
                  lableKey="name"
                  valueKey="value"
                  data-id="businessUnit"
                  data-auto-id="businessUnitSelect"
                  @change=${this.inputValueChange}
                  required
                  value=${this.formData.businessUnit}
                  ?disabled=${stringComparison(
          this.loggedUserType,
          "BusinessAdmin"
        ) ||
      (stringComparison(this.addProfType, "regular") &&
        stringComparison(
          this.formData.userType,
          "enterpriseadmin"
        ))}
                  warning="Business unit is required"
                  emptyOptionLabel="Select Business Unit"
                >
                </jha-form-select>
              </jha-tooltip>
            </div>
          </div>
          ${this.shouldShowCredentials ? html`
  <div id="rowRiv">
    <jha-form-row>
      <h2>Credentials required</h2>
    </jha-form-row>

    <div>
      <jha-form-radio-button
        data-auto-id="passwordRadioBtn"
        value="password"
                .checked=${this.formData.authType === "password" ||
        !stringComparison(
          stringComparison(this.addProfType, "regular")
            ? this.formData.userType
            : "participant",
          "participant"
        )}
        name="authType"
        @checked=${this.onAuthModeChange}
                ?disabled=${!stringComparison(
          stringComparison(this.addProfType, "regular")
            ? this.formData.userType
            : "participant",
          "participant"
        )}
      >
        <span id="radioSpan">
          <strong>Password</strong>

                  <p>
                    Send an automated email to the user to set the password.
                  </p>
        </span>
      </jha-form-radio-button>

      <jha-form-radio-button
        data-auto-id="sshKeyRadioBtn"
        value="ssh"
                .checked=${!(this.formData.authType === "password")}
        name="authType"
        @checked=${this.onAuthModeChange}
                ?disabled=${!stringComparison(
          stringComparison(this.addProfType, "regular")
            ? this.formData.userType
            : "participant",
          "participant"
        )}
      >
        <span id="radioSpan">
          <strong>SSH Public Key</strong>
          <p>Send an automated email to the user to set the SSH key.</p>
        </span>
      </jha-form-radio-button>
    </div>
  </div>
` : undefined}
            <div id="textFieldDiv">
              ${this.shouldShowFolderLocation
        ? html` <jha-form-row>
                      <h2>Folder Location</h2>
                    </jha-form-row>
                    <jha-tooltip
                      bottom
                      text=${this.folderLocationTooltip}
                    >
                      <div id="txtAreaDiv">
                        <jha-form-text-input
                          outline
                          .required=${!stringComparison(
          this.formData.userType,
          "enterpriseadmin"
        )}
                          placeholder=${stringComparison(
          this.addProfType,
          "destination"
        )
            ? "Eg: /FolderName/"
            : "Eg: /FolderName/Path/"}
                          label="Folder Location"
                          maxlength="256"
                          @change=${this.onFolderLocationChange}
                          data-id="homeDirectory"
                          data-auto-id="homeDirectoryInput"
                          warning="Folder Location is required"
                          type="text"
                          ?disabled=${stringComparison(
              this.formData.userType,
              "enterpriseadmin"
            )}
                        ></jha-form-text-input>
                      </div>
                    </jha-tooltip>`
        : undefined}
              <div id="rowDiv">
                <jha-form-row>
                  <h2>Notes</h2>
                </jha-form-row>
                <jha-tooltip
                  top-right
                  text="Enter comments and notes. Max length of 256 characters."
                >
                  <div id="textAreaDiv">
                    <jha-form-textarea-input
                      role="textbox"
                      placeholder="Enter comments and notes"
                      minlength="0"
                      maxlength="256"
                      type="text"
                      data-id="note"
                      data-auto-id="noteInput"
                      @change=${this.inputValueChange}
                      maxlength="256"
                    >
                    </jha-form-textarea-input>
                  </div>
                </jha-tooltip>
              </div>
            </div>
            <div id="btnCon">
              <jha-tooltip top text="Click to save and exit.">
                <jha-button
                  data-auto-id="addUserBtn"
                  type="submit"
                  primary
                  large
                  sync
                  ?disabled=${!this.formValid ||
      !this.isUserIdValid ||
      (!stringComparison(this.addProfType, "destination") &&
        !stringComparison(
          this.formData.userType,
          "enterpriseadmin"
        ) &&
        !stringComparison(
          this.formData.userType,
          "businessadmin"
        ) &&
        !this.isFolderLocationValid) ||
      (stringComparison(this.addProfType, "destination") &&
        !this.isFolderLocationValid)}
                  @click=${this.onAddUserClick}
                >
                  Save User
                </jha-button>
              </jha-tooltip>
              <jha-tooltip top text="Click to cancel and exit without saving.">
                <jha-button
                  data-auto-id="cancelBtn"
                  type="button"
                  outline
                  sync
                  @click=${this.onCancelClick}
                >
                  Cancel
                </jha-button>
              </jha-tooltip>
            </div>
          </div>
        </form>
      </div>
    `;
  }
}
declare global {
  interface HTMLElementTagNameMap {
    "add-new-user-form": AddNewUserForm;
  }
}
